import type { Material } from '@babylonjs/core/Materials/material';
import { PBRMaterial } from '@babylonjs/core/Materials/PBR/pbrMaterial';
import { RawTexture } from '@babylonjs/core/Materials/Textures/rawTexture';
import { copyFactorTextureSettings } from './factorTexture';

/**
 * The transparency an editor preview gave a copy of its own: what the copy
 * returns to when a Renderer's opacity comes back to 1, in place of anything
 * authored. Kept on the copy's metadata under {@link OWNS_TRANSPARENCY}.
 */
export interface OwnedTransparency {
    alpha: number;
    transparencyMode: number | null;
    needDepthPrePass: boolean;
}

/**
 * Metadata key an editor preview sets on a copy whose alpha and transparency
 * mode it writes. Nothing on such a copy is authored: a Renderer records no
 * authored transparency from it, and when its opacity returns to 1 it puts
 * the owner's values back rather than any it recorded.
 */
export const OWNS_TRANSPARENCY = 'traceTransparencyOwned';

/** The transparency another owner gave `material`, or null for a material nobody owns that way. */
export function transparencyOwnedElsewhere(material: Material): OwnedTransparency | null {
    const owned = (material.metadata as Record<string, unknown> | null | undefined)?.[OWNS_TRANSPARENCY];
    return owned && typeof owned === 'object' ? (owned as OwnedTransparency) : null;
}

/** Drop the claim on `material`: it is no longer a preview's copy, and what it carries is its own. */
export function releaseTransparency(material: Material): void {
    const metadata = material.metadata as Record<string, unknown> | null;
    if (!metadata?.[OWNS_TRANSPARENCY]) return;
    const { [OWNS_TRANSPARENCY]: _dropped, ...rest } = metadata;
    material.metadata = rest;
}

/**
 * Metadata key a Renderer sets on a material the first time its opacity
 * drives it: the alpha, transparency mode and depth pre-pass the material had
 * then, which is what the file said. Anything that copies the material while
 * the Renderer drives it reads its authored state here, not off the driven
 * fields.
 */
export const AUTHORED_TRANSPARENCY = 'traceAuthoredTransparency';

/** Stamp `material`'s current transparency as its authored state, once. */
export function recordAuthoredTransparency(material: Material): void {
    const metadata = (material.metadata as Record<string, unknown> | null) ?? {};
    if (metadata[AUTHORED_TRANSPARENCY]) return;
    const authored: OwnedTransparency = {
        alpha: material.alpha,
        transparencyMode: material.transparencyMode,
        needDepthPrePass: material.needDepthPrePass,
    };
    material.metadata = { ...metadata, [AUTHORED_TRANSPARENCY]: authored };
}

/** Drop the authored stamp: the material's fields are authored again. */
export function clearAuthoredTransparency(material: Material): void {
    const metadata = material.metadata as Record<string, unknown> | null;
    if (!metadata?.[AUTHORED_TRANSPARENCY]) return;
    const { [AUTHORED_TRANSPARENCY]: _dropped, ...rest } = metadata;
    material.metadata = rest;
}

/** `material`'s transparency as the file said it: the Renderer's record when it drove the material, else its current values. */
export function authoredTransparencyOf(material: Material): OwnedTransparency {
    const recorded = (material.metadata as Record<string, unknown> | null | undefined)?.[AUTHORED_TRANSPARENCY];
    if (recorded && typeof recorded === 'object') return recorded as OwnedTransparency;
    return { alpha: material.alpha, transparencyMode: material.transparencyMode, needDepthPrePass: material.needDepthPrePass };
}

/** Record on `material` the transparency its owner just gave it (see {@link OWNS_TRANSPARENCY}). */
export function claimTransparency(material: Material): void {
    const owned: OwnedTransparency = {
        alpha: material.alpha,
        transparencyMode: material.transparencyMode,
        needDepthPrePass: material.needDepthPrePass,
    };
    material.metadata = { ...((material.metadata as Record<string, unknown> | null) ?? {}), [OWNS_TRANSPARENCY]: owned };
}

/** Clone a material with its new texture objects tied to its disposal. */
export function cloneMaterial<T extends Material>(source: T, name: string): ReturnType<T['clone']> {
    const clone = source.clone(name);
    if (clone) {
        // Packed factor copies share pixels when cloned, but RawTexture resets
        // its wrapper. Preserve the current look before a projection or style
        // owner can make further intentional edits to the clone.
        if (source instanceof PBRMaterial && clone instanceof PBRMaterial
            && source.metallicTexture instanceof RawTexture && clone.metallicTexture instanceof RawTexture) {
            copyFactorTextureSettings(source.metallicTexture, clone.metallicTexture);
        }
        // Only new texture objects belong to the clone; render targets can stay shared.
        const shared = new Set(source.getActiveTextures());
        const owned = new Set(clone.getActiveTextures().filter(texture => !shared.has(texture)));
        clone.onDisposeObservable.addOnce(() => {
            for (const texture of owned) texture.dispose();
        });
    }
    return clone as ReturnType<T['clone']>;
}
